import { useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useSmallerThanMD } from 'components/shared/hooks/useMediaQuery';
import {
  animate,
  motion,
  type PanInfo,
  type Transition,
  useMotionValue,
  useReducedMotion,
} from 'framer-motion';

import type { MarketplaceProperty } from '../types';

import similarCardStyles from './PdpSimilarListingCard.module.css';

const FLOOR_SLIDE: Transition = { type: 'spring', visualDuration: 0.2, bounce: 0 };
const ARROW_CLASS_NAME = cn(
  'pointer-events-none absolute top-1/2 z-1 hidden size-7 -translate-y-1/2 rounded-full p-0 opacity-0 md:flex',
  'transition-opacity duration-150 motion-reduce:transition-none',
  'group-hover/floor-plans:pointer-events-auto group-hover/floor-plans:opacity-100',
  'group-focus-within/floor-plans:pointer-events-auto group-focus-within/floor-plans:opacity-100',
  'focus-visible:pointer-events-auto focus-visible:opacity-100',
  'disabled:cursor-default',
  'group-hover/floor-plans:disabled:opacity-30 group-focus-within/floor-plans:disabled:opacity-30',
);

export const PdpFloorPlans = ({ property }: { property: MarketplaceProperty }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMobile = useSmallerThanMD();
  const reduceMotion = useReducedMotion();
  const transition: Transition = reduceMotion ? { duration: 0 } : FLOOR_SLIDE;
  const carouselRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragOffset = useMotionValue(0);
  const photos = property.floorPlanPhotos
    .map((photo, index) => ({
      ...photo,
      label: photo.level?.label ?? `Floor ${index + 1}`,
      sequence: photo.level?.sequence ?? index + 1,
    }))
    .toSorted(
      (a, b) =>
        Number(a.level?.isBelowGround === true) - Number(b.level?.isBelowGround === true) ||
        a.sequence - b.sequence,
    );
  const plan = photos[selectedIndex];
  if (!plan) return null;
  const canSwipe = isMobile && photos.length > 1;
  const selectAdjacentFloor = (direction: number) =>
    setSelectedIndex((index) => Math.max(0, Math.min(photos.length - 1, index + direction)));

  const finishSwipe = (event: PointerEvent, { offset }: PanInfo) => {
    const threshold = (viewportRef.current?.clientWidth ?? 0) / 7;
    if (
      event.type !== 'pointercancel' &&
      Math.abs(offset.x) > Math.max(threshold, Math.abs(offset.y))
    ) {
      selectAdjacentFloor(offset.x < 0 ? 1 : -1);
    }
    void animate(dragOffset, 0, transition);
  };

  return (
    <Col as="section" aria-label="Floorplan" className="gap-6">
      <Text as="h2" className="text-label-lg">
        Floorplan
      </Text>
      {photos.length > 1 && (
        <Row
          aria-label="Choose a floor plan"
          className="gap-2 overflow-x-auto rounded-xl bg-od-bg-secondary p-2"
        >
          {photos.map((photo, index) => (
            <Button
              key={`${photo.url}-${index}`}
              analyticsName="cosmos-marketplace-pdp-refresh-floor-plan-select"
              aria-label={photo.label}
              aria-pressed={selectedIndex === index}
              variant="secondary"
              size="sm"
              onClick={() => setSelectedIndex(index)}
              className={cn(
                'relative isolate h-auto min-h-16 min-w-max flex-1 flex-col items-start justify-start gap-1 rounded-lg border-0 px-3 py-3 text-left',
                'transition-none not-disabled:active:scale-100',
                'bg-transparent not-disabled:hover:bg-transparent not-disabled:active:bg-transparent',
                selectedIndex !== index && '!text-od-content-secondary',
              )}
            >
              {selectedIndex === index && (
                <motion.span
                  aria-hidden="true"
                  layoutId="pdp-floor-indicator"
                  transition={transition}
                  className="absolute inset-0 -z-10 rounded-lg bg-od-bg-primary"
                />
              )}
              <Text as="span" className="text-label-sm">
                {photo.label}
              </Text>
              {photo.statistics?.livingSquareFootage != null && (
                <Text as="span" className="max-w-full text-body-sm text-od-content-secondary">
                  {photo.statistics.livingSquareFootage.toLocaleString('en-US')} sqft
                </Text>
              )}
            </Button>
          ))}
        </Row>
      )}
      <Box
        ref={carouselRef}
        as="section"
        aria-label="Floorplan images"
        tabIndex={photos.length > 1 ? 0 : undefined}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          event.currentTarget.focus();
          selectAdjacentFloor(event.key === 'ArrowLeft' ? -1 : 1);
        }}
        className="group/floor-plans relative w-full rounded-lg focus-visible:outline-2 focus-visible:outline-od-border-focused-secondary"
      >
        <motion.div
          ref={viewportRef}
          className={cn(
            'w-full overflow-hidden rounded-lg border border-od-border-primary',
            canSwipe ? 'touch-pan-y' : 'touch-auto',
          )}
          onPanStart={
            canSwipe
              ? () => {
                  carouselRef.current?.focus({ preventScroll: true });
                  dragOffset.stop();
                }
              : undefined
          }
          onPan={
            canSwipe
              ? (_, { offset }) => {
                  if (Math.abs(offset.x) > Math.abs(offset.y)) {
                    const atEdge =
                      (selectedIndex === 0 && offset.x > 0) ||
                      (selectedIndex === photos.length - 1 && offset.x < 0);
                    dragOffset.set(atEdge ? offset.x / 4 : offset.x);
                  }
                }
              : undefined
          }
          onPanEnd={canSwipe ? finishSwipe : undefined}
        >
          <motion.div style={{ x: dragOffset }}>
            <motion.div
              initial={false}
              animate={{ x: `-${selectedIndex * 100}%` }}
              transition={transition}
              className="flex w-full"
            >
              {photos.map((photo, index) => (
                <Box
                  key={`${photo.url}-${index}`}
                  aria-hidden={index !== selectedIndex}
                  className="w-full shrink-0 items-center justify-center py-4"
                >
                  <Image
                    src={photo.url}
                    alt={photo.label}
                    draggable={false}
                    className="max-h-128 w-full select-none object-contain"
                    loading="lazy"
                  />
                </Box>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
        {photos.length > 1 && (
          <>
            <Button
              variant="secondary"
              aria-label="Previous floor"
              analyticsName="cosmos-marketplace-pdp-refresh-floor-plan-previous"
              disabled={selectedIndex === 0}
              onClick={() => selectAdjacentFloor(-1)}
              onPointerUp={(event) => event.currentTarget.blur()}
              className={cn('left-3', ARROW_CLASS_NAME, similarCardStyles.glassButton)}
            >
              <IconChevronLeft size={18} aria-hidden />
            </Button>
            <Button
              variant="secondary"
              aria-label="Next floor"
              analyticsName="cosmos-marketplace-pdp-refresh-floor-plan-next"
              disabled={selectedIndex === photos.length - 1}
              onClick={() => selectAdjacentFloor(1)}
              onPointerUp={(event) => event.currentTarget.blur()}
              className={cn('right-3', ARROW_CLASS_NAME, similarCardStyles.glassButton)}
            >
              <IconChevronRight size={18} aria-hidden />
            </Button>
          </>
        )}
      </Box>
    </Col>
  );
};
